import { inject, Injectable } from '@angular/core';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { coerceArray, coerceBoolean, coerceNumber, coerceStringArray } from './coercion';
import { RmaLocalStorageKey } from './local-storage-keys';

type LocalStorageKey = RmaLocalStorageKey | (string & {});

@Injectable({
  providedIn: 'root',
})
export class RmaLocalStorageService {
  private readonly localStorage = inject(WA_LOCAL_STORAGE);

  public getItem(key: LocalStorageKey): string | null {
    return this.localStorage?.getItem(key) ?? null;
  }

  public getBoolean(key: LocalStorageKey): boolean | null {
    const value = this.getItem(key);

    return value === null ? null : coerceBoolean(value);
  }

  public getNumber(key: LocalStorageKey): number | null {
    const value = this.getItem(key);

    return value === null ? null : coerceNumber(value);
  }

  public getObject<T>(key: LocalStorageKey): T | null {
    const value = this.getItem(key);

    return value === null ? null : JSON.parse(value);
  }

  public getArray<T>(key: LocalStorageKey): T[] | null {
    const value = this.getObject<T>(key);

    return value === null ? null : coerceArray<T>(value);
  }

  public getStringArray(key: LocalStorageKey, separator: string | RegExp = /\s+/): string[] | null {
    const value = this.getObject<unknown>(key);

    return value === null ? null : coerceStringArray(value, separator);
  }

  public setItem<T>(key: LocalStorageKey, value: T): void {
    switch (typeof value) {
      case 'string':
        this.localStorage?.setItem(key, value);
        return;
      case 'boolean':
      case 'number':
      case 'bigint':
        this.localStorage?.setItem(key, String(value));
        return;
      case 'object':
        this.localStorage?.setItem(key, JSON.stringify(value));
        return;
      default:
        this.localStorage?.setItem(key, '');
        return;
    }
  }

  public removeItem(key: LocalStorageKey): void {
    this.localStorage?.removeItem(key);
  }
}
